import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { SnackbarProvider, useSnackbar } from 'notistack';

import { DiceIcons, DiceResult } from './../../components';
import { DICE_TYPES } from './../../common/constants';
import {
	getAlienResultPresentation,
	isAlienDiceResult,
} from './../../common/gameSystems/alien/formatAlienResult';
import {
	customDicesState,
	customDiceSelectedState,
	dicesBonusListState,
	dicesSelectedState,
	isNextDiceExplosiveState,
	isNextDiceRepetitionState,
	repeatDicesState,
} from '../../common/recoil/GameSystem/gameSystemAtom';
import { diceResultsState, diceRollState } from '../../common/recoil/UserInfo/atoms';
import {
	DEFAULT_DICE_LABEL,
	getDiceColorPreset,
	normalizeDiceLabel,
} from '../../common/services/diceMetadata';

const useStyles = makeStyles((theme) => ({
	inlineDices: {
		position: 'absolute',
		'&::-webkit-scrollbar': {
			width: '0.4em',
			height: '0.1px',
		},
		'&::-webkit-scrollbar-track': {
			boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
			webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'rgba(0,0,0,.3)',
			outline: '1px solid rgba(255,255,255,.3)',
		},
		overflow: 'auto',
		height: '100%',
		display: 'flex',
		flexDirection: 'column-reverse',
	},

	dice: {
		background: "url('/images/d10.png') no-repeat center transparent",
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'center',
		height: '50px',
		width: '100px',
		backgroundSize: 'contain',
		color: 'white',
	},
	newDice: {
		position: 'relative',
	},
	clickable: {
		cursor: 'pointer',
	},
	notClickable: {
		cursor: 'arrow',
	},
	diceClicked: {
		backgroundColor: 'rgba(255, 255, 255, 0.3)!important',
	},
	newDiceSvg: {
		color: 'black',
	},
	newDiceSpan: {
		color: 'white',
		background: 'black',
		position: 'absolute',
		fontSize: '16px',
		left: '10px',
		top: '10px',
		width: '19px',
		textAlign: 'center',
		borderRadius: '50px',
	},
	customDice: {
		display: 'flex',
		background: 'white',
		color: 'black',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '40px',
		minWidth: '40px',
		padding: '0 8px',
		marginBottom: '5px',
	},
	diceNotation10: {
		clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0 51%)',
	},
	diceDifficultySuccess: {
		background: 'green',
	},
	diceDifficultyFail: {
		background: 'red',
	},
	diceDifficultyWarning: {
		background: 'orange',
	},
	diceAlienSummary: {
		background: '#2e7d32',
		color: '#ffffff',
	},
	diceAlienPanic: {
		background: '#f2b544',
		color: '#2f1900',
	},
	diceAlienFailure: {
		background: '#c62828',
		color: '#ffffff',
	},
}));

const UserCameraCustomDices = ({ diceResults }) => {
	const classes = useStyles();
	const { t, i18n } = useTranslation();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const [customDices, setCustomDices] = useRecoilState(customDicesState);
	const [customDiceSelected, setCustomDiceSelected] = useRecoilState(customDiceSelectedState);
	const [diceSelected, setDiceSelected] = useRecoilState(dicesSelectedState);
	const [dicesBonusList, setDicesBonusList] = useRecoilState(dicesBonusListState);
	const [isNextDiceExplosive, setIsNextDiceExplosive] = useRecoilState(isNextDiceExplosiveState);
	const [isNextDiceRepetition, setIsNextDiceRepetition] =
		useRecoilState(isNextDiceRepetitionState);
	const [repeatDices, setRepeatDices] = useRecoilState(repeatDicesState);
	const [previousSnackBars, setPreviousSnackBars] = useState([]);
	const [roll, setRoll] = useRecoilState(diceRollState);

	useEffect(() => {
		previousSnackBars.forEach((key) => closeSnackbar(key));
		if (repeatDices.length > 0) {
			enqueueSnackbar(
				t('diceResults.repeat.title', {
					diceNumber: repeatDices.length,
				}),
				{
					variant: 'success',
					anchorOrigin: {
						vertical: 'top',
						horizontal: 'center',
					},
					action: (key) => {
						setPreviousSnackBars([...previousSnackBars, key]);
						return (
							<Button
								onClick={() => {
									reroll(key);
								}}>
								Lanzar
							</Button>
						);
					},
				}
			);
		}
	}, [repeatDices]);

	useEffect(() => {
		setRepeatDices([]);
	}, [diceResults]);

	const currentCustomDiceConfig = useMemo(() => {
		return customDiceSelected ? customDices.find((x) => x.name === customDiceSelected) : [];
	}, [customDiceSelected]);

	const diceClicked = (result) => {
		if (result.repeatable) {
			if (repeatDices.some((dice) => dice.id === result.id)) {
				setRepeatDices([...repeatDices.filter((dice) => dice.id !== result.id)]);
			} else {
				setRepeatDices([...repeatDices, result]);
			}
		}
	};

	const reroll = (key) => {
		closeSnackbar(key);
		setIsNextDiceRepetition(true);
		if (customDiceSelected) {
			setRoll(repeatDices.map((x) => x.notation));
		} else {
			setRoll(
				repeatDices.map((x) => ({
					notation: x.notation,
					color: x.color || 'default',
					label: normalizeDiceLabel(x.label || DEFAULT_DICE_LABEL),
				}))
			);
		}
	};

	const CommonDice = ({ id, name, result }) => {
		const colorPreset = getDiceColorPreset(result.color);
		return (
			<Box
				className={`${classes.newDice} ${
					result.repeatable ? classes.clickable : classes.notClickable
				} `}
				key={name}
				title={result.label || DEFAULT_DICE_LABEL}
				onClick={() => diceClicked(result)}>
				<DiceIcons
					dice={result.notation}
					size="medium"
					color={colorPreset.iconColor}
				/>
				<span
					className={`${
						repeatDices.some((dice) => dice.id === id) ? classes.diceClicked : ''
					} ${classes.newDiceSpan}`}
					style={{
						background: colorPreset.badgeBackground,
						color: colorPreset.badgeColor,
					}}>
					<DiceResult
						notation={result.notation}
						result={result.value}
						type={result.type}
					/>
				</span>
			</Box>
		);
	};

	const CustomDice = ({ id, name, result }) => {
		const alienPresentation = isAlienDiceResult(result)
			? getAlienResultPresentation(result, {
					t,
					locale: i18n.resolvedLanguage || i18n.language,
			  })
			: null;
		const displayValue = alienPresentation?.displayValue || result.summary || result.value;
		const notationClass = `diceNotation${result.notation}`;
		const alienStateClass = result.panicRoll
			? Number(result.value) >= 10
				? 'diceAlienFailure'
				: 'diceAlienPanic'
			: result.summary
				? result.panic
					? 'diceAlienPanic'
					: result.value > 0
						? 'diceAlienSummary'
						: 'diceAlienFailure'
			: null;
		const successClass = `diceDifficulty${
			result.success === false
				? result.partialSuccess === true
					? 'Warning'
					: 'Fail'
				: 'Success'
		}`;
		const stateClass = alienStateClass ? classes[alienStateClass] : classes[successClass];
		const customClasses = `${classes.customDice} ${classes[notationClass]} ${
			stateClass
		} ${repeatDices.some((dice) => dice.id === id) ? classes.diceClicked : ''}`;
		return (
			<Box
				className={customClasses}
				key={name}
				title={alienPresentation?.title || result.summary || result.value}>
				{displayValue}
			</Box>
		);
	};

	const RenderDice = ({ name, result }) => {
		switch (result.type) {
			case DICE_TYPES.CUSTOM:
				return <CustomDice id={result.id} name={`custom-${name}`} result={result} />;
				break;
			default:
				return <CommonDice id={result.id} name={name} result={result} />;
		}
	};

	return (
		<>
			{diceResults?.map((result, index) => {
				if (result) {
					if (Array.isArray(result)) {
						return result.map((x, subIndex) => {
							return <RenderDice name={`dice-${index}-${subIndex}`} result={x} />;
						});
					} else if (result instanceof Object) {
						return <RenderDice name={`dice-${index}`} result={result} />;
					} else {
						return <Box className={classes.dice}> {result} </Box>;
					}
				}
			})}
		</>
	);
};

export default UserCameraCustomDices;
